// Landing Page Component for Gift-Wish - Premium Elegant Night Design
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Gift, Share2, Lock, Clock, ChevronRight, Sparkles, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage, useAuth } from "@/contexts";
import { translations, eventTypes } from "@/i18n";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import AnimatedBackground from "@/components/AnimatedBackground";
import AuthModal from "@/AuthModal";

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const { t, language } = useLanguage();
  const [showAuthModal, setShowAuthModal] = useState(false);

  const handleGetStarted = () => {
    if (user) {
      navigate("/dashboard");
    } else {
      setShowAuthModal(true);
    }
  };

  const handleAuthSuccess = (userData) => {
    setUser(userData);
    navigate("/dashboard");
  };

  const authTranslations = translations[language] || translations.en;

  const features = [
    { icon: Gift, title: t("feature_gifts"), desc: t("feature_gifts_desc") },
    { icon: Share2, title: t("feature_share"), desc: t("feature_share_desc") },
    { icon: Lock, title: t("feature_reserve"), desc: t("feature_reserve_desc") },
    { icon: Clock, title: t("feature_countdown"), desc: t("feature_countdown_desc") },
  ];

  const displayEventTypes = eventTypes.slice(0, 8);

  return (
    <div className="noise-bg min-h-screen flex flex-col" data-testid="landing-page">
      <AnimatedBackground />
      <Header onLoginClick={() => setShowAuthModal(true)} />
      
      <main className="flex-grow">
        {/* Hero Section - Premium */}
        <section className="relative py-24 md:py-36 px-6 overflow-hidden">
          {/* Gradient orbs */}
          <div className="absolute top-20 left-[10%] w-72 h-72 bg-primary/20 rounded-full blur-[120px] pointer-events-none" />
          <div className="absolute bottom-20 right-[10%] w-96 h-96 bg-primary/10 rounded-full blur-[150px] pointer-events-none" />
          
          <div className="max-w-7xl mx-auto relative z-10">
            <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
              <div className="lg:col-span-7 animate-fade-in-up">
                <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.1] mb-8">
                  {t("hero_title")}
                  <span className="text-primary block mt-2 animate-fade-in-up animation-delay-200">
                    {t("hero_title_accent")}
                  </span>
                </h1>
                
                <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-12 max-w-xl">
                  {t("hero_description")}
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    size="lg"
                    className="premium-btn h-14 px-8 text-lg font-medium rounded-xl text-white animate-glow-pulse"
                    onClick={handleGetStarted}
                    data-testid="hero-cta-btn"
                  >
                    {t("create_list")}
                    <ChevronRight className="ml-2 w-5 h-5" />
                  </Button>
                  <Button 
                    size="lg"
                    variant="outline"
                    className="btn-outline-light h-14 px-8 text-lg font-medium rounded-xl"
                    onClick={() => document.getElementById("features").scrollIntoView({ behavior: "smooth" })}
                    data-testid="learn-more-btn"
                  >
                    {t("learn_more")}
                  </Button>
                </div>

                {/* Trust indicators with Premium badge */}
                <div className="mt-12 pt-8 border-t border-border/30">
                  {/* Premium badge above trust */}
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-surface border border-border/50 mb-6 animate-fade-in animation-delay-300">
                    <Sparkles className="w-4 h-4 text-primary" />
                    <span className="text-sm text-muted-foreground">Premium Wishlist Platform</span>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <div className="flex -space-x-2">
                      {[1,2,3,4].map(i => (
                        <div key={i} className="w-10 h-10 rounded-full bg-surface-2 border-2 border-background flex items-center justify-center">
                          <Star className="w-4 h-4 text-gold" />
                        </div>
                      ))}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{t("trusted_by") || "Trusted by thousands"}</p>
                      <p className="text-xs text-muted-foreground">{t("happy_users") || "of happy users"}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Hero Visual */}
              <div className="lg:col-span-5 relative animate-scale-in animation-delay-200">
                
                
                
              >
                <div className="relative">
                  {/* Main card */}
                  <div className="glass-card rounded-3xl p-6 shadow-card">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-4">
                        <div
                          
                          className="rounded-2xl overflow-hidden shadow-lg"
                        >
                          <img 
                            src="https://images.unsplash.com/photo-1549465220-1a8b9238cd48?w=400&h=500&fit=crop" 
                            alt="Gift" 
                            className="w-full h-48 object-cover"
                          />
                        </div>
                        <div className="rounded-2xl overflow-hidden shadow-lg hover:scale-[1.02] transition-transform">
                          <img 
                            src="https://images.unsplash.com/photo-1513885535751-8b9238bd345a?w=400&h=300&fit=crop" 
                            alt="Celebration" 
                            className="w-full h-32 object-cover"
                          />
                        </div>
                      </div>
                      <div className="space-y-4 pt-8">
                        <div className="rounded-2xl overflow-hidden shadow-lg hover:scale-[1.02] transition-transform">
                          <img 
                            src="https://images.unsplash.com/photo-1607344645866-009c320b63e0?w=400&h=300&fit=crop" 
                            alt="Party" 
                            className="w-full h-32 object-cover"
                          />
                        </div>
                        <div className="rounded-2xl overflow-hidden shadow-lg hover:scale-[1.02] transition-transform">
                          <img 
                            src="https://static.prod-images.emergentagent.com/jobs/db7cdd2f-00f2-46b1-985e-01519213d594/images/bc735529e9ee80d1c74b44ca783975120afdb76deeba0bd2143c05cfe76bbc48.png" 
                            alt="Gift Box" 
                            className="w-full h-48 object-cover"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section id="features" className="py-24 px-6 relative">
          <div className="absolute inset-0 bg-surface/50" />
          <div className="max-w-7xl mx-auto relative z-10">
            <div className="text-center mb-20">
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-6">
                {t("how_it_works")}
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                {t("simple_process")}
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              
              
              
              
            >
              {features.map((feature, i) => (
                <div
                  key={i}
                  
                >
                  <div 
                    className="card-premium rounded-2xl p-8 h-full group"
                    data-testid={`feature-card-${i}`}
                  >
                    <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                      <feature.icon className="w-7 h-7 text-primary" />
                    </div>
                    <h3 className="font-heading text-xl font-semibold mb-3 tracking-tight">{feature.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Event Types */}
        <section className="py-24 px-6">
          <div className="max-w-7xl mx-auto">
            <div 
              className="text-center mb-16"
              
              
              
            >
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-6">
                {t("for_any_occasion")}
              </h2>
              <p className="text-muted-foreground text-lg">
                {t("not_only_birthday")}
              </p>
            </div>

            <div 
              className="flex flex-wrap justify-center gap-4"
              
              
              
              
            >
              {displayEventTypes.map((event, i) => (
                <div
                  key={event.type}
                  
                  
                  className="cursor-default"
                >
                  <div className={`event-${event.type} border rounded-full px-6 py-3.5 flex items-center gap-3 backdrop-blur-sm`}>
                    <span className="text-2xl">{event.emoji}</span>
                    <span className="font-medium">{t(`event_${event.type}`)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-6 relative overflow-hidden">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px] pointer-events-none" />
          
          <div className="max-w-3xl mx-auto text-center relative z-10">
            <div
              
              
              
              
            >
              <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-8">
                {t("ready_to_create")}
              </h2>
              <p className="text-muted-foreground text-lg md:text-xl mb-12 max-w-xl mx-auto">
                {t("join_free")}
              </p>
              <Button 
                size="lg"
                className="premium-btn h-16 px-12 text-lg font-medium rounded-xl text-white animate-glow-pulse"
                onClick={handleGetStarted}
                data-testid="cta-btn"
              >
                {t("start_free")}
                <Sparkles className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      
      <AuthModal 
        open={showAuthModal} 
        onOpenChange={setShowAuthModal}
        onSuccess={handleAuthSuccess}
        translations={authTranslations}
        language={language}
      />
    </div>
  );
};

export default LandingPage;
